import dayjs, { Dayjs } from "dayjs";
import { BaseFilter, ComparatorKey, comparators, FilterValue } from "src/constants";
import { getValue } from "./objects";

const handlers = {
  number: (val: number | string | boolean | null | undefined): number => {
    if (val == null) return NaN;
    if (typeof val === "boolean") return val ? 1 : 0;
    const n = Number(val);
    return isNaN(n) ? NaN : n;
  },

  date: (val: string | Date | Dayjs | null | undefined): Dayjs | null => {
    if (val == null) return null;
    if (dayjs.isDayjs(val)) return val;
    if (val instanceof Date) return dayjs(val);
    const parsed = dayjs(val);
    return parsed.isValid() ? parsed : null;
  },

  boolean: (val: number | string | boolean | null | undefined): boolean => {
    if (val == null) return false;
    if (typeof val === "string") return val.toLowerCase() === "true" || val === "1";
    return Boolean(val);
  },

  string: (val: number | string | boolean | Dayjs | null | undefined): string => {
    if (val == null) return "";
    if ((val as Dayjs)?.format) return (val as Dayjs).format();
    return String(val);
  },
  "string|number[]": (val: any): string[] => {
    if (!Array.isArray(val)) return val == null ? [] : [String(val)];
    return val.map(String);
  },
};

const compileGetter = (path: (string | number)[]) => (obj: any) => path.reduce((o, k) => o?.[k], obj);

type Getter<T> = (obj: T) => any;

interface GroupOption<T> {
  key: string;
  path: (string | number)[];
  category?: "number" | "string" | "boolean" | "date"|"string|number[]";
  transform?: (value: any) => any;
}

function groupBy<T, G extends GroupOption<T>[]>(data: T[], groupingOptions: G): Map<G[number]["key"], Map<any, T[]>> {
  const preparedGroups = groupingOptions.map((group) => ({
    ...group,
    getter: (obj: T) => {
      let value: any = compileGetter(group.path)(obj);
      if (group.transform) value = group.transform(value);
      if (group.category && (handlers as any)[group.category]) {
        value = handlers[group.category](value);
      }
      return value;
    },
  }));

  const groupsMap = new Map<G[number]["key"], Map<any, T[]>>();

  for (const group of preparedGroups) {
    groupsMap.set(group.key as G[number]["key"], new Map());
  }

  for (const element of data) {
    for (const group of preparedGroups) {
      const value = group.getter(element);
      const groupMap = groupsMap.get(group.key as G[number]["key"])!;

      const keys: any[] = Array.isArray(value) ? value : [value];
      for (const key of keys) {
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key)!.push(element);
      }
    }
  }

  return groupsMap;
}

const applyFilter =
  (filters: BaseFilter[], filterValues: Record<string, FilterValue<any>>) =>
  (item: any): boolean =>
    Object.entries(filterValues).every(([key, filterEntry]) => {
      const filter = filters.find((f) => f.key === key);
      if (!filter) return false;
      let value: any = filter.transform ? filter.transform(getValue(item, filter.path)) : getValue(item, filter.path);
      if (filter.category && handlers[filter.category]) {
        value = handlers[filter.category](value);
      }

      const comparator = comparators[filter.op as ComparatorKey];
      if (!comparator) return false;
      if (filter.op === "between") {
        return comparator(value, {
          valueFrom: (filterEntry as any).valueFrom,
          valueTo: (filterEntry as any).valueTo,
        });
      } else {
        const rawFilterValue = (filterEntry as any).value;
        if (Array.isArray(rawFilterValue)) {
          if (Array.isArray(value)) return rawFilterValue.some((rv) => value.includes(rv));
          return rawFilterValue?.includes(value);
        }
        return comparator(value, rawFilterValue);
      }
    });

const applySorter = (filters: BaseFilter[], sorter: any) => (items: any[]) => {
  const filter = filters?.find((f) => f?.key === sorter?.key);
  if (!filter) return items;

  return [...items].sort((a, b) => {
    let aValue: any = filter.transform ? filter.transform(getValue(a, filter.path)) : getValue(a, filter.path);
    let bValue: any = filter.transform ? filter.transform(getValue(b, filter.path)) : getValue(b, filter.path);

    if (filter.category && handlers[filter.category]) {
      aValue = handlers[filter.category](aValue);
      bValue = handlers[filter.category](bValue);
    }

    if (aValue == null && bValue != null) return sorter.asc ? -1 : 1;
    if (aValue != null && bValue == null) return sorter.asc ? 1 : -1;
    if (aValue == null && bValue == null) return 0;

    if (aValue > bValue) return sorter.asc ? 1 : -1;
    if (aValue < bValue) return sorter.asc ? -1 : 1;
    return 0;
  });
};

export { applyFilter, applySorter, groupBy, handlers };
